import * as React from 'react';
import {
  makeStyles,
  tokens,
  Title3,
  Text,
  Button,
  Dropdown,
  Option,
  Spinner,
} from '@fluentui/react-components';
import { useProject } from '../../../hooks/useProject';
import { ArrowClockwise24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
  },
  description: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    textAlign: 'left',
    lineHeight: tokens.lineHeightBase200,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    alignItems: 'stretch',
    width: '100%',
  },
  selectorRow: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  selectorLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  selector: {
    width: '100%',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
  },
  button: {
    width: '100%',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    width: '100%',
  },
  projectTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  projectDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
});

interface ProjectSelectorProps {
  onProjectSelect: (projectId: number) => void;
  onRefreshDiagnosis?: () => void;
  selectedProjectId?: number;
  loading?: boolean;
  selectedProjectName?: string;
  loadingMessages?: string[];
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onProjectSelect,
  onRefreshDiagnosis,
  selectedProjectId,
  loading = false,
  selectedProjectName,
  loadingMessages = [],
}) => {
  const styles = useStyles();
  const { projects, listLoading, listError, fetchProjects } = useProject();
  const [selectedValue, setSelectedValue] = React.useState<string>(
    selectedProjectId?.toString() || ''
  );
  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);

  React.useEffect(() => {
    if (loading && loadingMessages.length > 0) {
      const interval = setInterval(() => {
        setCurrentMessageIndex((prevIndex) =>
          (prevIndex + 1) % loadingMessages.length
        );
      }, 2000);
      return () => clearInterval(interval);
    } else {
      setCurrentMessageIndex(0);
    }
  }, [loading, loadingMessages.length]);

  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  React.useEffect(() => {
    if (selectedProjectId) {
      setSelectedValue(selectedProjectId.toString());
    }
  }, [selectedProjectId]);

  // Encontrar o projeto selecionado para exibir código e descrição
  const selectedProject = projects.find(p => p.id.toString() === selectedValue);
  const displayValue = selectedProject 
    ? `${selectedProject.id} - ${selectedProject.descricao}`
    : '';

  const handleProjectChange = (event: any, data: any) => {
    const newValue = data.selectedOptions[0] || '';
    setSelectedValue(newValue);
    
    if (newValue) {
      onProjectSelect(parseInt(newValue));
    }
  };

  const handleRefreshDiagnosis = () => {
    if (onRefreshDiagnosis) {
      onRefreshDiagnosis();
    } else if (selectedValue) {
      onProjectSelect(parseInt(selectedValue));
    }
  };

  if (listLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Spinner size="medium" />
          <Text>Carregando projetos...</Text>
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3 className={styles.title}>Erro ao carregar projetos</Title3>
          <Text className={styles.description}>
            {listError.message || 'Ocorreu um erro ao carregar a lista de projetos.'}
          </Text>
          <Button appearance="primary" onClick={fetchProjects}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Selecionar Projeto para Diagnóstico</Title3>
        <Text className={styles.description}>
          Escolha um projeto para iniciar o diagnóstico de uso da aplicação.
          Será analisado automaticamente o percentual de atingimento dos eventos configurados.
        </Text>
      </div>

      {projects.length > 0 && (
        <div className={styles.formSection}>
          <div className={styles.selectorRow}>
            <Dropdown
              className={styles.selector}
              placeholder="Selecione um projeto"
              value={displayValue}
              selectedOptions={selectedValue ? [selectedValue] : []}
              onOptionSelect={handleProjectChange}
              disabled={loading}
            >
              {projects.map((project) => (
                <Option 
                  key={project.id} 
                  value={project.id.toString()}
                  text={`${project.id} - ${project.descricao} (${project.integracaoEmpresa})`}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr 120px',
                    gap: tokens.spacingHorizontalM,
                    alignItems: 'center',
                    padding: `${tokens.spacingVerticalXS} 0`,
                    minWidth: '400px'
                  }}>
                    <Text style={{
                      fontWeight: 'semibold',
                      fontSize: tokens.fontSizeBase200,
                      color: tokens.colorNeutralForeground1
                    }}>
                      #{project.id}
                    </Text>
                    <Text style={{
                      fontSize: tokens.fontSizeBase300,
                      color: tokens.colorNeutralForeground1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {project.descricao}
                    </Text>
                    <Text style={{
                      fontSize: tokens.fontSizeBase200,
                      color: tokens.colorNeutralForeground3,
                      textAlign: 'right'
                    }}>
                      {project.integracaoEmpresa}
                    </Text>
                  </div>
                </Option>
              ))}
            </Dropdown>
          </div>
          <div className={styles.actions}>
            <Button
              appearance="primary"
              size="large"
              onClick={handleRefreshDiagnosis}
              disabled={!selectedValue || loading}
              className={styles.button}
              icon={loading ? undefined : <ArrowClockwise24Regular />}
            >
              {loading && loadingMessages.length > 0
                ? loadingMessages[currentMessageIndex]
                : loading
                ? 'Analisando...'
                : 'Gerar novo diagnóstico'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelector;